import React, { useEffect, useMemo, useRef, useState } from 'react';
import { DocumentForm } from '../../documents/form/DocumentForm';
import { DocumentProvider } from '../../documents/DocumentContext';
import { DocumentSchema } from '../../documents/types';
import { createEmptyDocumentFunctionRegistry } from '../../documents/utils/functionRegistry';
import {
    DocumentSchemaRecord,
    getSchemaImpactCount,
} from '../../../services/documentSchemaService';
import { validateSchemaEditorInput } from './SchemaValidation';
import { EditorMode, SchemaEditorState, ValidationIssue } from './types';
import { Upload, Download, AlertTriangle } from 'lucide-react';

interface SchemaEditorProps {
    mode: EditorMode;
    schemaRecord?: DocumentSchemaRecord;
    baseRecord?: DocumentSchemaRecord;
    isSaving: boolean;
    onSave: (nextSchema: Partial<DocumentSchemaRecord>) => Promise<void>;
    onCreate: (
        record: Omit<DocumentSchemaRecord, '_id' | 'createdAt' | 'updatedAt'>
    ) => Promise<void>;
}

class PreviewErrorBoundary extends React.Component<
    { children: React.ReactNode },
    { hasError: boolean; errorMessage: string }
> {
    constructor(props: { children: React.ReactNode }) {
        super(props);
        this.state = { hasError: false, errorMessage: '' };
    }

    static getDerivedStateFromError(error: any) {
        return {
            hasError: true,
            errorMessage: error?.message || 'Preview failed to render',
        };
    }

    componentDidUpdate(prevProps: { children: React.ReactNode }) {
        if (prevProps.children !== this.props.children && this.state.hasError) {
            this.setState({ hasError: false, errorMessage: '' });
        }
    }

    render() {
        if (this.state.hasError) {
            return (
                <div className="border border-destructive/40 rounded p-4 text-sm text-destructive">
                    {this.state.errorMessage}
                </div>
            );
        }
        return this.props.children;
    }
}

const formatIssues = (issues: ValidationIssue[]) =>
    issues.map((issue, index) => (
        <li key={`${issue.code}-${issue.path}-${index}`}>
            <code>{issue.path}</code>: {issue.message}
        </li>
    ));

const previewDataSources = {
    scanners: [
        { label: 'Scanner A', value: 'scanner-a' },
        { label: 'Scanner B', value: 'scanner-b' },
    ],
    policies: [
        { label: 'Policy 1', value: 'policy-1' },
        { label: 'Policy 2', value: 'policy-2' },
    ],
    previousExams: ['Prior MRI Brain', 'Prior CT Head'],
    previousTasks: ['Implant evaluation', 'Review source documents'],
    implantSuggestions: [
        { label: 'Example Implant A', value: 'implant-a' },
        { label: 'Example Implant B', value: 'implant-b' },
    ],
};

const NEW_SCHEMA_TEMPLATE = {
    ownerType: 'nordinsight' as const,
    key: '',
    name: '',
    description: '',
    version: 1,
    schema: {
        id: '',
        title: '',
        description: '',
        sections: [] as any[],
    },
};

const buildInitialJson = (
    mode: EditorMode,
    schemaRecord?: DocumentSchemaRecord,
    baseRecord?: DocumentSchemaRecord
): string => {
    if (mode === 'edit' && schemaRecord) {
        const editable = { ...schemaRecord } as Record<string, any>;
        delete editable._id;
        delete editable.createdAt;
        delete editable.updatedAt;
        delete editable.__v;
        return JSON.stringify(editable, null, 2);
    }

    if (mode === 'create-version' && baseRecord) {
        const clone = { ...baseRecord } as Record<string, any>;
        delete clone._id;
        delete clone.createdAt;
        delete clone.updatedAt;
        delete clone.__v;
        clone.version = (baseRecord.version || 0) + 1;
        return JSON.stringify(clone, null, 2);
    }

    if (mode === 'clone' && baseRecord) {
        const clone = { ...baseRecord } as Record<string, any>;
        delete clone._id;
        delete clone.createdAt;
        delete clone.updatedAt;
        delete clone.__v;
        clone.key = '';
        clone.name = '';
        clone.version = 1;
        return JSON.stringify(clone, null, 2);
    }

    return JSON.stringify(NEW_SCHEMA_TEMPLATE, null, 2);
};

const getHeading = (
    mode: EditorMode,
    schemaRecord?: DocumentSchemaRecord,
    baseRecord?: DocumentSchemaRecord
): string => {
    if (mode === 'edit' && schemaRecord) {
        return `${schemaRecord.key} v${schemaRecord.version}`;
    }
    if (mode === 'create-version' && baseRecord) {
        return `New version of ${baseRecord.key}`;
    }
    if (mode === 'clone' && baseRecord) {
        return `Clone from ${baseRecord.key}`;
    }
    return 'New Schema';
};

export const SchemaEditor: React.FC<SchemaEditorProps> = ({
    mode,
    schemaRecord,
    baseRecord,
    isSaving,
    onSave,
    onCreate,
}) => {
    const fileInputRef = useRef<HTMLInputElement>(null);

    const initialRaw = useMemo(
        () => buildInitialJson(mode, schemaRecord, baseRecord),
        [mode, schemaRecord, baseRecord]
    );

    const [rawJson, setRawJson] = useState(initialRaw);
    const [editorState, setEditorState] = useState<SchemaEditorState>({
        rawJson: initialRaw,
        parsedRecord: null,
        validation: { errors: [], warnings: [], isValidForSave: false },
    });
    const [impactCount, setImpactCount] = useState<number | null>(null);

    const originalId =
        mode === 'edit' && schemaRecord ? schemaRecord._id : null;

    useEffect(() => {
        setRawJson(initialRaw);
        setEditorState({
            rawJson: initialRaw,
            parsedRecord: null,
            validation: { errors: [], warnings: [], isValidForSave: false },
        });
        setImpactCount(null);
    }, [initialRaw]);

    useEffect(() => {
        const timer = setTimeout(() => {
            const next = validateSchemaEditorInput(rawJson, originalId);
            setEditorState({
                rawJson,
                parsedRecord: next.parsedRecord,
                parseError: next.parseError,
                validation: next.validation,
            });
        }, 300);

        return () => clearTimeout(timer);
    }, [rawJson, originalId]);

    useEffect(() => {
        if (mode === 'edit' && schemaRecord) {
            getSchemaImpactCount(schemaRecord.key, schemaRecord.version)
                .then(setImpactCount)
                .catch(() => setImpactCount(null));
        }
    }, [mode, schemaRecord]);

    const previewSchema = editorState.parsedRecord?.schema as
        | DocumentSchema
        | undefined;

    const handleSave = async () => {
        if (!editorState.parsedRecord || !editorState.validation.isValidForSave) {
            return;
        }
        if (mode === 'edit') {
            await onSave(editorState.parsedRecord);
        } else {
            await onCreate(
                editorState.parsedRecord as Omit<
                    DocumentSchemaRecord,
                    '_id' | 'createdAt' | 'updatedAt'
                >
            );
        }
    };

    const handleUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = (e) => {
            const text = e.target?.result as string;
            try {
                const parsed = JSON.parse(text);
                setRawJson(JSON.stringify(parsed, null, 2));
            } catch {
                // Let the validator show the parse error
                setRawJson(text);
            }
        };
        reader.readAsText(file);
        event.target.value = '';
    };

    const handleDownload = () => {
        const blob = new Blob([rawJson], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        const parsed = editorState.parsedRecord;
        const filename = parsed?.key
            ? `${parsed.key}-v${parsed.version || 1}.json`
            : 'schema.json';
        a.download = filename;
        a.click();
        URL.revokeObjectURL(url);
    };

    const heading = getHeading(mode, schemaRecord, baseRecord);
    const isCreateMode = mode !== 'edit';
    const saveLabel = isCreateMode ? 'Create Schema' : 'Save Schema';

    return (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="space-y-3">
                <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 min-w-0">
                        <h3 className="font-semibold truncate">
                            {heading}
                        </h3>
                        {mode === 'edit' && impactCount !== null && impactCount > 0 && (
                            <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-orange-100 text-orange-700 whitespace-nowrap">
                                <AlertTriangle className="w-3 h-3" />
                                {impactCount} doc{impactCount !== 1 ? 's' : ''}
                            </span>
                        )}
                    </div>
                    <div className="flex items-center gap-1.5">
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept=".json"
                            className="hidden"
                            onChange={handleUpload}
                        />
                        <button
                            type="button"
                            onClick={() => fileInputRef.current?.click()}
                            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs rounded border border-input bg-background hover:bg-accent"
                            title="Upload JSON"
                        >
                            <Upload className="w-3.5 h-3.5" />
                            Upload
                        </button>
                        <button
                            type="button"
                            onClick={handleDownload}
                            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs rounded border border-input bg-background hover:bg-accent"
                            title="Download JSON"
                        >
                            <Download className="w-3.5 h-3.5" />
                            Download
                        </button>
                        <button
                            type="button"
                            onClick={handleSave}
                            disabled={
                                isSaving || !editorState.validation.isValidForSave
                            }
                            className="px-3 py-1.5 text-xs rounded bg-primary text-primary-foreground disabled:opacity-50"
                        >
                            {isSaving ? 'Saving...' : saveLabel}
                        </button>
                    </div>
                </div>
                <textarea
                    value={rawJson}
                    onChange={(e) => setRawJson(e.target.value)}
                    className="w-full h-[60vh] font-mono text-xs border rounded p-3 bg-background"
                    spellCheck={false}
                />
                <div className="border rounded p-3 space-y-2 text-sm">
                    <div>
                        <strong>Validation</strong>:{' '}
                        {editorState.validation.errors.length} error(s),{' '}
                        {editorState.validation.warnings.length} warning(s)
                    </div>
                    {editorState.parseError && (
                        <div className="text-destructive">
                            Parse error: {editorState.parseError}
                        </div>
                    )}
                    {editorState.validation.errors.length > 0 && (
                        <div>
                            <div className="font-medium text-destructive">
                                Errors
                            </div>
                            <ul className="list-disc ml-5 text-destructive">
                                {formatIssues(editorState.validation.errors)}
                            </ul>
                        </div>
                    )}
                    {editorState.validation.warnings.length > 0 && (
                        <div>
                            <div className="font-medium text-orange-600">
                                Warnings
                            </div>
                            <ul className="list-disc ml-5 text-orange-600">
                                {formatIssues(editorState.validation.warnings)}
                            </ul>
                        </div>
                    )}
                </div>
            </div>

            <div className="space-y-3">
                <h3 className="font-semibold">
                    Live Form Preview (No API Functions)
                </h3>
                <div className="border rounded p-4 max-h-[80vh] overflow-auto">
                    {previewSchema ? (
                        <PreviewErrorBoundary>
                            <DocumentProvider
                                initialData={{}}
                                documentId="preview"
                                saveDocument={async () => ({
                                    documentId: 'preview',
                                    status: 'draft',
                                })}
                                completeDocument={async () => ({
                                    documentId: 'preview',
                                    status: 'completed',
                                })}
                            >
                                <DocumentForm
                                    schema={previewSchema}
                                    dataSources={previewDataSources}
                                    functions={createEmptyDocumentFunctionRegistry()}
                                />
                            </DocumentProvider>
                        </PreviewErrorBoundary>
                    ) : (
                        <div className="text-sm text-muted-foreground">
                            Fix JSON errors to render preview.
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};
